import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router';
import {
  useFloraCapabilities,
  useFloraUserPreferences,
  useUpdateFloraUserPreferences,
} from '../../../../api/agents-client/agent-client.hooks';
import type {
  ApprovalMode,
  FloraCapabilitiesResponse,
  FloraUserPreferencesResponse,
  ToolLoadingMode,
} from '../../../../api/agents-client/agent-client.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { clearFloraDraftApprovalMode } from '../../../../store/global-store/global-store.actions';
import {
  computePreferencesPayload,
  getDefaultDraft,
  mergeSettingsWithCatalog,
} from '../agent-customize/agent-customize.helpers';
import type { CustomizeDraft, CustomizeHandlers } from '../agent-customize/agent-customize.type';
import { SaveState } from '../shared/settings/settings.type';

/** How long "Saved" stays up after the last save settles. */
const SAVED_INDICATOR_MS = 2500;

interface FloraToolSettings {
  capabilities: FloraCapabilitiesResponse | undefined;
  /** The saved settings merged over the catalog defaults; null until both have loaded, or if either failed. */
  draft: CustomizeDraft | null;
  isLoading: boolean;
  error: Error | null;
  saveState: SaveState;
  /** Per-tool handlers in the shape the capability components already take. */
  handlers: CustomizeHandlers;
  setToolLoading: (mode: ToolLoadingMode) => void;
  setApprovalMode: (mode: ApprovalMode) => void;
  setReferenceChatHistory: (enabled: boolean) => void;
  /** Restores every tool, approval, approval mode, and tool loading to the catalog defaults (and saves); keeps reference chat history. */
  resetToDefaults: () => void;
}

/**
 * Reads and autosaves the settings Customize used to hold behind a Save
 * button: tool on/off, per-tool approvals, approval mode, tool loading, and
 * chat-history referencing.
 *
 * Responsibilities:
 * 1. Merge the saved preferences over the capability catalog into a draft
 * 2. On every change, cancel in-flight preference fetches, write the next
 *    draft into the cache optimistically, and save it (saves queue one at a time)
 * 3. On a failure, show the error toast, and once the queue settles refetch
 *    the saved state and report Error
 * 4. Once a changed default is saved, drop a new-chat approval mode left on the composer
 * 5. Report Saving / Saved / Error for the page's status note, and warn before
 *    the tab closes while saves are still queued
 *
 * Only the Customize-owned keys are sent, so the profile form and the learned
 * preferences list (which write other keys of the same object) are untouched.
 *
 * @returns {FloraToolSettings} The draft, handlers, and save state
 */
function useFloraToolSettings(): FloraToolSettings {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const { capabilities, isLoading: isCatalogLoading, error: catalogError } = useFloraCapabilities();
  const { preferences, isLoading: isPreferencesLoading, error: preferencesError } = useFloraUserPreferences();
  const { mutateAsync: savePreferences } = useUpdateFloraUserPreferences();

  const [saveState, setSaveState] = useState<SaveState>(SaveState.Idle);
  const pendingSavesRef = useRef(0);
  // Whether any save in the current run of queued saves failed; reported once the run settles.
  const runFailedRef = useRef(false);
  const savedTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
    },
    [],
  );

  // No draft when the saved preferences failed to load: a draft built from the
  // catalog defaults alone would let the next change save those defaults over
  // the user's real settings.
  const draft = useMemo(
    () =>
      capabilities && !isPreferencesLoading && !preferencesError
        ? mergeSettingsWithCatalog(capabilities, preferences)
        : null,
    [capabilities, preferences, isPreferencesLoading, preferencesError],
  );

  // The draft each change builds on. While saves are queued it holds the
  // latest local change (the cache can briefly lag behind it, or be overwritten
  // by an unrelated refetch), so rapid successive changes build on each other.
  // Once every save has settled it follows the cache again. Synced after each
  // commit rather than during render, where React forbids writing refs.
  const draftRef = useRef(draft);
  useLayoutEffect(() => {
    if (pendingSavesRef.current === 0) {
      draftRef.current = draft;
    }
  });

  // Queued saves reach the server one after another, so closing or reloading
  // the tab mid-run can drop a change the page already shows as made.
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (saveState !== SaveState.Saving) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [saveState],
    ),
  );

  const update = useCallback(
    (mutate: (current: CustomizeDraft) => CustomizeDraft) => {
      const current = draftRef.current;
      if (!capabilities || !current) return;

      const next = mutate(current);
      draftRef.current = next;
      const payload = computePreferencesPayload(capabilities, next);
      const queryKey = ['flora-user-preferences', organizationId];
      const approvalModeChanged = next.approvalMode !== current.approvalMode;

      pendingSavesRef.current += 1;
      if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
      setSaveState(SaveState.Saving);

      const save = async () => {
        // Stop an in-flight preferences fetch first: a cancelled fetch puts the
        // cache back to its pre-fetch state when it settles, which would undo
        // the optimistic write below.
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData<FloraUserPreferencesResponse>(queryKey, (previous) => ({
          preferences: { ...previous?.preferences, ...payload },
        }));
        try {
          // `mutateAsync` settles per call; per-call `mutate` callbacks would
          // only fire for the latest of several queued saves.
          await savePreferences(payload);
          // A newly saved default should take effect on the next new chat, so
          // drop a mode left on the new-chat composer (chats the user switched keep theirs).
          if (approvalModeChanged) clearFloraDraftApprovalMode();
        } catch {
          runFailedRef.current = true;
          notifications.show({ message: 'Failed to save settings', color: 'red' });
        }
        // Runs after either outcome (the catch doesn't rethrow); not a `finally`,
        // which the React Compiler can't compile.
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) {
          if (runFailedRef.current) {
            runFailedRef.current = false;
            setSaveState(SaveState.Error);
            // The optimistic cache no longer matches the server; reload it.
            void queryClient.invalidateQueries({ queryKey: ['flora-user-preferences'] });
          } else {
            setSaveState(SaveState.Saved);
            savedTimerRef.current = window.setTimeout(() => setSaveState(SaveState.Idle), SAVED_INDICATOR_MS);
          }
        }
      };
      void save();
    },
    [capabilities, organizationId, queryClient, savePreferences],
  );

  const handlers = useMemo<CustomizeHandlers>(
    () => ({
      setTools: (names, value) =>
        update((current) => {
          const enabledTools = { ...current.enabledTools };
          for (const name of names) enabledTools[name] = value;
          return { ...current, enabledTools };
        }),
      setApprovals: (names, value) =>
        update((current) => {
          const approvals = { ...current.approvals };
          for (const name of names) approvals[name] = value;
          return { ...current, approvals };
        }),
    }),
    [update],
  );

  const setToolLoading = useCallback(
    (mode: ToolLoadingMode) => update((current) => ({ ...current, toolLoading: mode })),
    [update],
  );
  const setApprovalMode = useCallback(
    (mode: ApprovalMode) => update((current) => ({ ...current, approvalMode: mode })),
    [update],
  );
  const setReferenceChatHistory = useCallback(
    (enabled: boolean) => update((current) => ({ ...current, referenceChatHistory: enabled })),
    [update],
  );

  // Reference chat history is a Memory-page privacy choice, not a tool
  // default, so a reset from Behavior leaves it as the user set it.
  const resetToDefaults = useCallback(() => {
    if (!capabilities) return;
    update((current) => ({ ...getDefaultDraft(capabilities), referenceChatHistory: current.referenceChatHistory }));
  }, [capabilities, update]);

  return {
    capabilities,
    draft,
    // A failed preferences load leaves no draft but isn't loading: report the error instead.
    isLoading: isCatalogLoading || isPreferencesLoading || (!!capabilities && !draft && !preferencesError),
    error: catalogError ?? preferencesError ?? null,
    saveState,
    handlers,
    setToolLoading,
    setApprovalMode,
    setReferenceChatHistory,
    resetToDefaults,
  };
}

export { useFloraToolSettings };
export type { FloraToolSettings };
